import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  LightBulbIcon, PlusIcon, TrashIcon, PencilSquareIcon, CalendarDaysIcon,
  ChevronLeftIcon, ChevronRightIcon, CheckIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { confirmDialog } from '../components/ConfirmDialog';

const MM = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const CATEGORIES = [
  { value: 'real', label: 'Trabajo real', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { value: 'edu', label: 'Educativo', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'marca', label: 'Marca', cls: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300' },
];
const FORMATS = [
  { value: 'post', label: 'Post' },
  { value: 'reel', label: 'Reel' },
  { value: 'historia', label: 'Historia' },
  { value: 'carrusel', label: 'Carrusel' },
];
const catMeta = (v: string) => CATEGORIES.find(c => c.value === v) || CATEGORIES[0];
const fmtMeta = (v: string) => FORMATS.find(f => f.value === v) || FORMATS[0];

interface Idea {
  id: string;
  title: string;
  category: string;
  format: string;
  notes?: string | null;
  tags?: string[];
  status: 'pending' | 'asignada';
  assignedDay?: number | null;
  assignedMonth?: number | null;
  assignedYear?: number | null;
}

// ── Mini calendario (mismo patrón que el banco de medios) ──
function monthGrid(year: number, month: number) {
  const off = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const dim = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < off; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return cells;
}

const MiniCalendar: React.FC<{ sel: { y: number; m: number; d: number } | null; onPick: (y: number, m: number, d: number) => void }> = ({ sel, onPick }) => {
  const today = new Date();
  const [vy, setVy] = useState(sel?.y ?? today.getFullYear());
  const [vm, setVm] = useState(sel?.m ?? today.getMonth() + 1);
  const prev = () => { if (vm === 1) { setVm(12); setVy(vy - 1); } else setVm(vm - 1); };
  const next = () => { if (vm === 12) { setVm(1); setVy(vy + 1); } else setVm(vm + 1); };
  const isToday = (d: number) => today.getDate() === d && today.getMonth() + 1 === vm && today.getFullYear() === vy;
  const [marked, setMarked] = useState<Set<number>>(new Set());
  useEffect(() => {
    let dead = false;
    axios.get('/api/content-calendars/days-with-content', { params: { year: vy, month: vm } })
      .then(r => { if (!dead) setMarked(new Set(r.data.days || [])); })
      .catch(() => { if (!dead) setMarked(new Set()); });
    return () => { dead = true; };
  }, [vy, vm]);
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={prev}><ChevronLeftIcon className="h-4 w-4" /></Button>
        <span className="text-sm font-semibold">{MM[vm - 1]} {vy}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={next}><ChevronRightIcon className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-1">
        {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {monthGrid(vy, vm).map((d, i) => d === null ? <div key={i} /> : (
          <button key={i} onClick={() => onPick(vy, vm, d)}
            className={`relative h-8 rounded-md text-sm transition-colors ${sel && sel.y === vy && sel.m === vm && sel.d === d ? 'bg-black text-white dark:bg-white dark:text-black font-semibold' : isToday(d) ? 'ring-1 ring-blue-500/50 hover:bg-muted' : 'hover:bg-muted'}`}>
            {d}
            {marked.has(d) && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${sel && sel.y === vy && sel.m === vm && sel.d === d ? 'bg-white dark:bg-black' : 'bg-emerald-500'}`} />}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> días con contenido planificado</p>
    </div>
  );
};

const Badge: React.FC<{ cls?: string; children: React.ReactNode }> = ({ cls = 'bg-muted text-muted-foreground', children }) => (
  <span className={`inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{children}</span>
);

const ContentIdeas: React.FC = () => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'asignada'>('pending');
  const [search, setSearch] = useState('');

  // asignar
  const [assigning, setAssigning] = useState<Idea | null>(null);
  const [assignSel, setAssignSel] = useState<{ y: number; m: number; d: number } | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/content-calendars/ideas', { params: { status: filter } });
      setIdeas(r.data.ideas || []);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudieron cargar las ideas');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Atajo "N" para nueva idea (ignora si estás escribiendo en un campo)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if ((e.key === 'n' || e.key === 'N') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault(); navigate('/content-ideas/new');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const removeIdea = async (idea: Idea) => {
    if (!(await confirmDialog({ title: 'Eliminar idea', description: `¿Eliminar "${idea.title}"?`, confirmText: 'Eliminar', destructive: true }))) return;
    try {
      await axios.delete(`/api/content-calendars/ideas/${idea.id}`);
      load();
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo eliminar'); }
  };

  const openAssign = (idea: Idea) => {
    const t = new Date();
    setAssignSel({ y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() });
    setAssigning(idea);
  };

  const doAssign = async () => {
    if (!assigning || !assignSel) return;
    setAssignBusy(true);
    try {
      await axios.post(`/api/content-calendars/ideas/${assigning.id}/assign`, { year: assignSel.y, month: assignSel.m, day: assignSel.d });
      toast.success(`Idea asignada al ${assignSel.d}/${assignSel.m}/${String(assignSel.y).slice(-2)}`);
      setAssigning(null); setAssignSel(null);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo asignar');
    } finally { setAssignBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? ideas.filter(i =>
        i.title.toLowerCase().includes(q) ||
        (i.notes || '').toLowerCase().includes(q) ||
        (i.tags || []).some(t => t.toLowerCase().includes(q)))
    : ideas;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div className="flex items-center gap-2">
          <LightBulbIcon className="h-6 w-6 text-amber-500" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Banco de ideas</h2>
            <p className="text-sm text-muted-foreground">Ideas de contenido sin fecha. Cuando estén listas, asignalas a un día del calendario.</p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/content-ideas/new')}
          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          size="sm"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nueva idea
          <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">N</kbd>
        </Button>
      </div>

      {/* Buscador */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder="Buscar por título, nota o etiqueta…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 w-full sm:w-[320px] lg:w-[520px] pl-8"
        />
      </div>

      {/* Filtro */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['pending', 'asignada'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === f ? 'border-amber-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {f === 'pending' ? 'Pendientes' : 'Ya asignadas'}
          </button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Cargando…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <LightBulbIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">
            {q ? 'No hay ideas que coincidan con la búsqueda.'
              : filter === 'pending' ? 'No hay ideas pendientes. Tocá "Nueva idea" para agregar una.'
              : 'Todavía no asignaste ninguna idea.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(idea => {
            const cm = catMeta(idea.category);
            return (
              <Card key={idea.id} className={idea.status === 'asignada' ? 'opacity-75' : ''}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm leading-snug">{idea.title}</h3>
                    <div className="flex gap-1 shrink-0">
                      <Badge cls={cm.cls}>{cm.label}</Badge>
                      <Badge>{fmtMeta(idea.format).label}</Badge>
                    </div>
                  </div>
                  {idea.notes && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{idea.notes}</p>}
                  {idea.tags && idea.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {idea.tags.map((t, i) => <span key={i} className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">#{t}</span>)}
                    </div>
                  )}
                  {idea.status === 'asignada' ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                      <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                        <CheckIcon className="h-4 w-4" /> Asignada al {idea.assignedDay}/{idea.assignedMonth}/{String(idea.assignedYear).slice(-2)}
                      </span>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => removeIdea(idea)}>
                        <TrashIcon className="h-3.5 w-3.5 mr-1" /> Quitar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1 pt-1">
                      <Button size="sm" variant="default" className="h-8" onClick={() => openAssign(idea)}>
                        <CalendarDaysIcon className="h-4 w-4 mr-1" /> Asignar a fecha
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => navigate(`/content-ideas/${idea.id}/edit`)}><PencilSquareIcon className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeIdea(idea)}><TrashIcon className="h-4 w-4" /></Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal asignar */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && (setAssigning(null), setAssignSel(null))}>
        <DialogContent className="w-[95vw] max-w-sm">
          {assigning && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2"><CalendarDaysIcon className="h-5 w-5" /> Asignar a una fecha</h2>
              <p className="text-sm text-muted-foreground">"{assigning.title}" → se crea como pieza en el día que elijas.</p>
              <MiniCalendar sel={assignSel} onPick={(y, m, d) => setAssignSel({ y, m, d })} />
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => { setAssigning(null); setAssignSel(null); }}>Cancelar</Button>
                <Button onClick={doAssign} disabled={!assignSel || assignBusy}>
                  {assignBusy ? 'Asignando…' : assignSel ? `Asignar al ${assignSel.d}/${assignSel.m}` : 'Elegí un día'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentIdeas;
